import { createTheme } from "@mui/material/styles";
// @ts-ignore
import { breakpoints as bp } from "../breakpoints.ts";
import { alertClasses } from "@mui/material/Alert";
import { colors } from "./colors";
// @ts-ignore
import { palette } from "./palette.ts";
import type { PaletteOptions } from "@mui/material/styles";

export const as = createTheme({
  palette: palette as PaletteOptions,
  customZIndex: {
    helpWidgetMaximized: 1600,
  },
  typography: {
    fontFamily: [
      '"Open Sans"',
      "sans-serif",
    ].join(","),
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    h1: {
      fontSize: "3rem",
      fontWeight: 700,
      lineHeight: 1.167,
    },
    h2: {
      fontSize: "2.5rem",
      fontWeight: 700,
    },
    h3: {
      fontSize: "2.125rem",
      fontWeight: 700,
    },
    h4: {
      fontSize: "1.75rem",
      fontWeight: 700,
    },
    h5: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 700,
    },
    body1: {
      fontSize: "1rem", // 16px
      lineHeight: 1.5,
    },
    body2: {
      fontSize: "0.875rem", // 14px
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 600,
    },
    caption: {
      fontSize: "0.75rem", // 12px
      lineHeight: 1.66,
    },
  },
  customPositions: {
    skipLink: {
      top: {
        xl: "20px",
        lg: "16px",
      },
      left: {
        xl: "20px",
        lg: "16px",
      },
    },
  },
  shape: {
    borderRadius: 1,
  },
  breakpoints: {
    values: {
      xs: bp.xs,
      sm: bp.sm,
      md: bp.md,
      lg: bp.lg,
      xl: bp.xl,
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingLeft: "15px",
          paddingRight: "15px",
          // maintain disableGutters prop functionality
          "&.MuiContainer-disableGutters": {
            paddingLeft: 0,
            paddingRight: 0,
          },
          // changes need to be applied per breakpoint to avoid the root being overwritten
          [theme.breakpoints.up("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
          },
          [theme.breakpoints.up("md")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
          },
          [theme.breakpoints.up("lg")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
          },
          [theme.breakpoints.up("xl")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
          },
        }),
        // changing the max-width for the container when set to 'fixed' (not fluid, which is default)
        fixed: ({ theme }) => ({
          maxWidth: "100%",
          [theme.breakpoints.up("sm")]: {
            maxWidth: "100%",
          },
          [theme.breakpoints.up("md")]: {
            maxWidth: "100%",
          },
          [theme.breakpoints.up("lg")]: {
            maxWidth: "1290px",
          },
          [theme.breakpoints.up("xl")]: {
            maxWidth: "1630px",
          },
        }),
        maxWidthLg: ({ theme }) => ({
          [theme.breakpoints.up("lg")]: {
            maxWidth: "1290px",
          },
        }),
        maxWidthXl: ({ theme }) => ({
          [theme.breakpoints.up("xl")]: {
            maxWidth: "1630px",
          },
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        [`&.${alertClasses.standard}.${alertClasses.colorSuccess}`]: {
          backgroundColor: colors.green[50],
          color: palette.text.primary,
        },
        [`&.${alertClasses.outlined}.${alertClasses.colorSuccess}`]: {
          color: palette.success.main,
        },
        [`&.${alertClasses.standard}.${alertClasses.colorError}`]: {
          backgroundColor: colors.red[50],
          color: palette.text.primary,
        },
        [`&.${alertClasses.outlined}.${alertClasses.colorSuccess}`]: {
          color: palette.error.main,
        },
        [`&.${alertClasses.standard}.${alertClasses.colorInfo}`]: {
          backgroundColor: colors.blue[50],
          color: palette.text.primary,
        },
        [`&.${alertClasses.outlined}.${alertClasses.colorInfo}`]: {
          color: palette.info.main,
        },
        [`&.${alertClasses.standard}.${alertClasses.colorWarning}`]: {
          backgroundColor: colors.orange[50],
          color: palette.text.primary,
        },
        [`&.${alertClasses.outlined}.${alertClasses.colorWarning}`]: {
          color: palette.warning.main,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          "&.Mui-focusVisible": {
            outline: "2px solid black",
            outlineOffset: "2px",
          },
          "&.Mui-focusVisibleOnDark:focus-visible": {
            outline: "2px solid white",
            outlineOffset: "2px",
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          padding: "8px 22px",
        },
        sizeLarge: () => ({
          padding: "10px 28px",
          fontSize: "0.9375rem",
          "& .MuiButton-startIcon > *:nth-of-type(1), & .MuiButton-endIcon > *:nth-of-type(1)":
          {
            fontSize: "24px", // Manter direto - não é um tamanho de texto padrão
          },
        }),
        text: {
          textDecoration: "underline",
          "&:hover": {
            textDecoration: "underline",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        label: ({ theme }) => ({
          fontWeight: theme.typography.fontWeightMedium,
        }),
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontWeight: theme.typography.fontWeightMedium,
          "&.MuiPaginationItem-outlinedPrimary": {
            color: palette.primary.main,
          },
          "&.MuiPaginationItem-textPrimary": {
            color: palette.primary.main,
            "&.Mui-selected": {
              color: palette.primary.contrastText,
            },
          },
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: "42px",
          padding: "6px 20px",
          width: "100%",
          fontSize: theme.typography.body1.fontSize,
          "&.Mui-focusVisible, &.Mui-selected": {
            backgroundColor: palette.action.selected,
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: palette.text.primary,
        },
        outlined: {
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& fieldset legend": {
            fontSize: theme.typography.body2.fontSize,
            maxWidth: "100%",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 3,
          },
        }),
        notchedOutline: {
          borderColor: palette.outlineInput.default,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: () => ({
          "&.Mui-focusVisible": {
            outline: "none",
          },
        }),
      },
    },
  },
});

export default as;
